import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, map, of, switchMap, throwError } from 'rxjs';
import {
  AiChatApiError,
  AiChatAttachmentModel,
  AiChatConversationModel,
  AiChatConversationPageModel,
  AiChatFeedbackRating,
  AiChatMessageModel,
  AiChatMessagePageModel,
  AiChatMessageRole,
  AiChatStreamFrame,
  AiChatThreadModel
} from '@coreModels';
import { ApiConstants } from '@coreConstant';
import { environment } from '../../../../environments/environment';
import { ServiceTokenService } from '../service-token.service';
import { cardedProducts } from '../../utils/ai-chat-product-cards';

/**
 * The refusal envelope returned on a non-2xx response. Every field is optional: a gateway or
 * proxy failure can answer with HTML or an unrelated body, so nothing here is guaranteed.
 */
interface ApiErrorEnvelope {
  message?: string;
  payload?: { retry_at?: string | null; retry_after_seconds?: number | null };
  error_details?: { request_uuid?: string | null };
}

/** One presigned S3 POST policy, returned by `attachments/uploads`. */
interface AttachmentUploadTicket {
  attachment_uuid: string;
  post_url: string;
  /** Signature and policy fields; every one must be posted, ahead of `file`. */
  fields: Record<string, string>;
  expires_in: number;
}

/** One stored conversation row, as `conversations` returns it. */
interface RawConversation {
  uuid: string;
  title?: string | null;
  message_count?: number;
  created_at?: string;
  updated_at?: string;
}

/** One stored message, as `conversations/{uuid}/messages` returns it. */
interface RawStoredMessage {
  uuid: string;
  role: string;
  content?: string;
  sequence: number;
  created_at?: string;
  feedback?: { rating?: AiChatFeedbackRating | null } | null;
  attachments?: {
    attachment_uuid: string;
    url?: string | null;
    file_name: string;
    content_type: string;
    size_bytes: number;
    width?: number | null;
    height?: number | null;
  }[];
  /** The same payloads the stream sent as `component` frames, e.g. product cards. */
  components?: unknown[] | null;
}

/** The outcome of validating one uploaded image, from `attachments/confirm`. */
interface ConfirmedAttachment {
  attachment_uuid: string;
  status: string;
  url?: string | null;
  file_name: string;
  content_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  error?: string | null;
}

/**
 * Pebbl customer chat.
 *
 * One endpoint - `POST {aiApiUrl}public/chat` - which answers the signed-in customer and
 * streams the reply back as Server-Sent Events, scoped to that customer's whole company
 * (their colleagues' quotes and orders too, not just their own).
 *
 * WHY fetch() AND NOT HttpClient
 * ------------------------------
 * Angular's HttpClient buffers the whole response body before it emits, so an SSE reply would
 * only appear once the agent had finished - the opposite of what this widget needs. fetch()
 * hands back a ReadableStream we can decode frame by frame, so the answer types itself out as
 * the agent produces it. The trade-off is that httpInterceptor does not run for these calls,
 * so URL prefixing, auth headers and error toasts are all handled here explicitly.
 *
 * AUTH
 * ----
 * Two headers, both required and meaning different things: `Authorization: Bearer <token>`
 * proves who the caller is, `X-Auth-Client: frontend` tells the service which public key
 * verifies that token's signature. A valid token with the client header missing fails closed
 * at 401 - it does not fall back to the other key.
 *
 * The website's own access token lives in an HttpOnly cookie the browser will not release to
 * JS and which, being host-scoped, would not reach this service anyway. ServiceTokenService
 * already solves exactly this for the cross-host Node API: it holds the token in memory,
 * fetched from the cookie-protected /oauth/service-token endpoint. We reuse it here.
 *
 * CONVERSATION IDENTITY
 * ---------------------
 * `conversation_uuid` is entirely ours to generate and hold - the server neither allocates nor
 * returns one. Reuse it across a session's turns for the agent's memory to carry forward.
 */
@Injectable({
  providedIn: 'root'
})
export class AiChatService {

  /** The API caps `message` at 2000 characters; the composer enforces the same limit. */
  static readonly MAX_MESSAGE_LENGTH = 2000;

  /* The service's declared bounds on the history query params, taken from its OpenAPI schema.
   *
   * THESE ARE REJECTIONS, NOT CLAMPS. FastAPI validates `limit` as ge=1/le=100 and answers 422
   * for anything outside it - it does not quietly cap the value and carry on. So every one of
   * these is enforced here before the request is built, and callers pass an intent rather than
   * a validated number. Asking for 200 messages in one call is what produced
   *   GET conversations/{uuid}/messages?limit=200 -> 422 Unprocessable Content
   * and silently emptied the transcript when a stored conversation was opened. */
  private static readonly MAX_PAGE_SIZE = 100;
  /** `search` is declared maxLength 200; a longer term is a 422, not a truncation. */
  private static readonly MAX_SEARCH_LENGTH = 200;
  /** What a transcript read asks for per page. The cap, since fewer pages means fewer calls. */
  private static readonly MESSAGE_PAGE_SIZE = 100;
  /**
   * How much of a transcript is restored at most. A stop, not an expectation: it bounds the
   * paging loop so a pathologically long conversation cannot fire calls indefinitely.
   */
  private static readonly MAX_RESTORED_MESSAGES = 500;

  /** Incrementing suffix for client-generated message ids (the API returns none). */
  private messageId = 0;

  constructor(
    private serviceTokenService: ServiceTokenService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  /**
   * Starts a conversation on a fresh `conversation_uuid`.
   *
   * Synchronous by design - there is no thread endpoint on this channel and no history to
   * restore, so there is nothing to wait for and no reason to flash a loading state.
   */
  startThread(): AiChatThreadModel {
    const thread = new AiChatThreadModel();
    thread.id = this.createConversationUuid();
    thread.messages = [];
    return thread;
  }

  /**
   * Sends one turn and emits the stream's frames as they arrive.
   *
   * Emits every frame the service sends, in order, and completes on `complete` (or when the
   * body ends). Unrecognised frame types are passed through untouched - callers ignore what
   * they do not know rather than failing on it.
   *
   * Unsubscribing aborts the in-flight request, so `takeUntil(destroy$)` in a component both
   * stops the UI updating and cancels the network call.
   *
   * @param conversationUuid the thread's client-generated v4 UUID, reused for every turn
   * @param messageUuid a FRESH client-generated v4 UUID for this turn. Required by the API -
   * omitting it is a 422. Fresh even on a regeneration: re-sending the previous turn's uuid
   * tells the service to overwrite the answer it stored, rather than keep both.
   * @param message the customer's text, already trimmed
   * @param regenerate re-runs the question that produced the answer already on screen. The
   * agent's memory is keyed by `conversation_uuid`, so without this flag the question it is
   * being asked again is one it can already see it has answered, and it simply repeats itself -
   * which is why Regenerate looked like it did nothing. The flag is what tells the service to
   * ask for another take instead of treating the turn as a new question.
   */
  streamMessage(
    conversationUuid: string,
    messageUuid: string,
    message: string,
    regenerate = false,
    attachmentUuids: string[] = []
  ): Observable<AiChatStreamFrame> {
    // Nothing to stream during SSR/prerender: the widget is browser-only and there is no
    // session on the server to answer as.
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new AiChatApiError({
        status: 0,
        apiMessage: 'The assistant is only available in the browser.'
      }));
    }

    // ensureToken() de-dupes concurrent fetches, so a send fired immediately after login does
    // not race ahead of the token request.
    return this.serviceTokenService.ensureToken().pipe(
      switchMap((token: string | null) =>
        this.openStream(conversationUuid, messageUuid, message, token, regenerate,
          attachmentUuids))
    );
  }

  /**
   * Builds a message object in the shape the UI renders. Ids and timestamps are ours: this
   * channel returns neither.
   */
  buildMessage(role: AiChatMessageRole, message: string): AiChatMessageModel {
    const chatMessage = new AiChatMessageModel();
    chatMessage.id = `${role}-${++this.messageId}-${Date.now()}`;
    chatMessage.role = role;
    chatMessage.message = message;
    chatMessage.created_at = new Date().toISOString();
    return chatMessage;
  }

  /**
   * Drops the in-memory access token so the next turn re-fetches it. Called after a 401: the
   * auth cookie is often still valid and only the cached token has gone stale.
   */
  invalidateToken(): void {
    this.serviceTokenService.clear();
  }

  /**
   * Files a thumbs-down as a review item - `POST {aiApiUrl}feedback`, the same endpoint and
   * body the console posts. Both fields are required by the API.
   *
   * It records only the category and the free-text message; the conversation and the rated Q&A
   * ride inside `message` as an encoded footer (see encodeAiChatFeedbackMessage) because this
   * endpoint keeps no link back to either. The thumb state itself is a separate call - see
   * setFeedback - and the two are independent: the review item survives an un-vote.
   *
   * @param category one of AI_CHAT_FEEDBACK_CATEGORIES
   * @param message the customer's note with the context footer already appended
   */
  createFeedback(category: string, message: string): Observable<void> {
    return this.authedRequest(ApiConstants.AI_CHAT_FEEDBACK_URL, {
      method: 'POST',
      body: JSON.stringify({ category, message })
    });
  }

  /**
   * Sets, or replaces, the thumbs rating on one reply - `PUT {aiApiUrl}feedback/rating`.
   *
   * The service resolves the owning identity from the bearer token and the `X-Auth-Client`
   * header, and 404s unless that identity owns the conversation. That works for this surface
   * only because the customer chat channel now persists real conversations against the
   * signed-in user; before it did, this call could not have succeeded from here at all.
   */
  setFeedback(
    conversationUuid: string,
    messageUuid: string,
    rating: AiChatFeedbackRating
  ): Observable<void> {
    return this.authedRequest(ApiConstants.AI_CHAT_FEEDBACK_RATING_URL, {
      method: 'PUT',
      body: JSON.stringify({
        conversation_uuid: conversationUuid,
        message_uuid: messageUuid,
        rating
      })
    });
  }

  /** Clears any thumbs rating from a reply (un-vote). Ids go in the query string, not a body. */
  clearFeedback(conversationUuid: string, messageUuid: string): Observable<void> {
    const params = new URLSearchParams({
      conversation_uuid: conversationUuid,
      message_uuid: messageUuid
    });
    return this.authedRequest(
      `${ApiConstants.AI_CHAT_FEEDBACK_RATING_URL}?${params.toString()}`,
      { method: 'DELETE' }
    );
  }

  /* ############### Conversation history ############### */

  /**
   * One page of this customer's stored conversations, newest first.
   *
   * The service scopes the list to the identity on the bearer token, so there is nothing to
   * filter client-side and no way to ask for anyone else's.
   *
   * Every parameter is bounded here before the URL is built - see MAX_PAGE_SIZE. The service
   * rejects an out-of-range value rather than clamping it, so a caller's honest mistake would
   * otherwise be a 422 and an empty rail.
   *
   * @param cursor the previous page's `next_cursor`, or null for the first page. Opaque - pass
   * it back exactly as it arrived; it encodes a sort position, not an offset.
   * @param search case-insensitive title substring, applied server-side. Trimmed to the
   * service's 200-character maximum.
   * @param limit rows per page. Clamped to 1..100.
   */
  listConversations(
    cursor: string | null = null,
    search = '',
    limit = 20
  ): Observable<AiChatConversationPageModel> {
    const params = new URLSearchParams({ limit: String(this.boundPageSize(limit)) });
    if (cursor) {
      params.set('cursor', cursor);
    }
    const term = search.trim().slice(0, AiChatService.MAX_SEARCH_LENGTH);
    if (term) {
      params.set('search', term);
    }

    return this.authedJson<{ items?: RawConversation[]; next_cursor?: string | null }>(
      `${ApiConstants.AI_CHAT_CONVERSATIONS_URL}?${params.toString()}`,
      { method: 'GET' }
    ).pipe(map((payload) => ({
      items: (payload?.items ?? []).map((item) => this.mapConversation(item)),
      next_cursor: payload?.next_cursor ?? null
    })));
  }

  /**
   * One page of a stored transcript, oldest first.
   *
   * Restores the messages the way the widget renders them, including each turn's `message_uuid`
   * and thumbs rating - without those a restored reply could not be re-rated or regenerated.
   *
   * Attachments come back with a freshly presigned `url`, minted per request; they are never
   * stored, so a URL held from a previous visit would already be dead.
   *
   * @param conversationUuid the conversation to read
   * @param cursor the previous page's `next_cursor` (a sequence number), or null to start.
   * The service declares this as an integer >= 1, so anything else is dropped rather than sent -
   * a 0 or a fractional cursor is a 422, and there is no page it could have meant.
   * @param limit messages per page. Clamped to 1..100.
   */
  listMessages(
    conversationUuid: string,
    cursor: number | null = null,
    limit = AiChatService.MESSAGE_PAGE_SIZE
  ): Observable<AiChatMessagePageModel> {
    const params = new URLSearchParams({ limit: String(this.boundPageSize(limit)) });
    if (cursor != null && Number.isInteger(cursor) && cursor >= 1) {
      params.set('cursor', String(cursor));
    }

    return this.authedJson<{ items?: RawStoredMessage[]; next_cursor?: number | null }>(
      `${ApiConstants.AI_CHAT_CONVERSATIONS_URL}/${encodeURIComponent(conversationUuid)}` +
      `/messages?${params.toString()}`,
      { method: 'GET' }
    ).pipe(map((payload) => ({
      items: (payload?.items ?? []).map((item) => this.mapStoredMessage(item)),
      next_cursor: payload?.next_cursor ?? null
    })));
  }

  /**
   * Reads a whole stored transcript, oldest first, following the cursor across pages.
   *
   * The page size is capped at 100 by the service, so a conversation longer than that cannot be
   * fetched in one call - asking for more is a 422, not a bigger page. Opening a conversation
   * wants the entire transcript rather than the first page of it, so this walks the cursor until
   * the service stops handing one back.
   *
   * Bounded by MAX_RESTORED_MESSAGES so a runaway or a service that kept returning a cursor
   * cannot spin here. Hitting that bound truncates the *oldest-first* read, so what is dropped
   * is the newest tail - accepted because the bound is far above any real support conversation.
   *
   * Sequential, not parallel: each page's cursor is only known once the previous page has
   * arrived, so there is nothing to fan out.
   */
  listAllMessages(conversationUuid: string): Observable<AiChatMessageModel[]> {
    return this.readMessagesFrom(conversationUuid, null, []);
  }

  /** One step of the transcript walk. Recurses on the cursor until it runs out. */
  private readMessagesFrom(
    conversationUuid: string,
    cursor: number | null,
    collected: AiChatMessageModel[]
  ): Observable<AiChatMessageModel[]> {
    return this.listMessages(conversationUuid, cursor, AiChatService.MESSAGE_PAGE_SIZE).pipe(
      switchMap((page) => {
        const merged = [...collected, ...page.items];
        const isExhausted = page.next_cursor == null
          || merged.length >= AiChatService.MAX_RESTORED_MESSAGES
          // A page that returns nothing while still handing back a cursor would loop forever.
          || page.items.length === 0;
        return isExhausted
          ? of(merged)
          : this.readMessagesFrom(conversationUuid, page.next_cursor, merged);
      })
    );
  }

  /**
   * Holds a page size inside the service's declared 1..100.
   *
   * Silently corrective on purpose: the alternative is a 422 that reaches the customer as an
   * empty conversation, and no caller has a legitimate reason to ask for more than the service
   * will give. A non-finite value (NaN from a parsed input) falls back to the maximum.
   */
  private boundPageSize(limit: number): number {
    if (!Number.isFinite(limit)) {
      return AiChatService.MAX_PAGE_SIZE;
    }
    return Math.min(Math.max(Math.trunc(limit), 1), AiChatService.MAX_PAGE_SIZE);
  }

  /** Renames one conversation. The service caps the title at 200 characters. */
  renameConversation(
    conversationUuid: string,
    title: string
  ): Observable<AiChatConversationModel> {
    return this.authedJson<RawConversation>(
      `${ApiConstants.AI_CHAT_CONVERSATIONS_URL}/${encodeURIComponent(conversationUuid)}`,
      { method: 'PATCH', body: JSON.stringify({ title: title.slice(0, 200) }) }
    ).pipe(map((payload) => this.mapConversation(payload)));
  }

  /**
   * Deletes one conversation and, server-side, the images sent in it. Not recoverable, which is
   * why the caller confirms first.
   */
  deleteConversation(conversationUuid: string): Observable<void> {
    return this.authedRequest(
      `${ApiConstants.AI_CHAT_CONVERSATIONS_URL}/${encodeURIComponent(conversationUuid)}`,
      { method: 'DELETE' }
    );
  }

  /** Maps one stored conversation row onto the shape the rail renders. */
  private mapConversation(raw: RawConversation | null): AiChatConversationModel {
    return {
      id: raw?.uuid ?? '',
      // The service derives a title from the opening question, but a conversation whose first
      // turn was images only has nothing to derive one from.
      title: raw?.title?.trim() || 'New conversation',
      message_count: raw?.message_count ?? 0,
      created_at: raw?.created_at ?? '',
      updated_at: raw?.updated_at ?? raw?.created_at ?? ''
    };
  }

  /**
   * Maps one stored message onto the model the transcript renders.
   *
   * `id` is ours and only has to be unique for @for tracking; `message_uuid` is the service's
   * and is what feedback and regeneration are keyed by, so the two are deliberately separate.
   * Anything transient (streaming flags, tool activity) is absent by design - a stored message
   * is finished, and re-showing "Looking up the answer" against a completed reply would be a lie.
   */
  private mapStoredMessage(raw: RawStoredMessage): AiChatMessageModel {
    const message = new AiChatMessageModel();
    message.id = `stored-${raw.uuid}`;
    message.role = raw.role === 'assistant' ? 'assistant' : 'user';
    message.message = raw.content ?? '';
    message.message_uuid = raw.uuid;
    message.created_at = raw.created_at ?? new Date().toISOString();
    message.feedback = raw.feedback?.rating ?? null;
    if (raw.attachments?.length) {
      message.attachments = raw.attachments.map((attachment) => ({
        attachment_uuid: attachment.attachment_uuid,
        file_name: attachment.file_name,
        content_type: attachment.content_type,
        size_bytes: attachment.size_bytes,
        width: attachment.width ?? null,
        height: attachment.height ?? null,
        url: attachment.url ?? null
      }));
    }
    const products = cardedProducts(raw.components);
    if (products.length) {
      message.product_skus = products.map((product) => product.sku);
      message.product_links = products.map((product) => product.link);
      message.product_recommended = products.map((product) => product.recommended);
    }
    return message;
  }

  /**
   * Uploads one image and returns it ready to attach, or throws.
   *
   * Three steps, because the bytes never pass through the AI service: it authorises the upload,
   * the browser posts the file straight to S3 under that signed policy, and the service then
   * validates what actually landed (it re-reads the object's real size and sniffs its leading
   * bytes, so a mislabelled content_type is caught server-side rather than trusted).
   *
   *   1. POST attachments/uploads  -> attachment_uuid + a presigned POST policy
   *   2. POST <post_url>           -> multipart form to S3; `file` MUST be appended last
   *   3. POST attachments/confirm  -> promotes it to 'ready', or reports why it was rejected
   *
   * Left unconfirmed, an upload is swept as an orphan after 24h, so an abandoned pick costs
   * nothing. The caller aborts the whole chain by unsubscribing.
   *
   * @param conversationUuid the conversation the image will be sent in; need not exist yet
   * @param file the picked image, already validated for type/size by the caller
   */
  uploadAttachment(conversationUuid: string, file: File): Observable<AiChatAttachmentModel> {
    return this.authedJson<{ items: AttachmentUploadTicket[] }>(
      ApiConstants.AI_CHAT_ATTACHMENT_UPLOADS_URL,
      {
        method: 'POST',
        body: JSON.stringify({
          conversation_uuid: conversationUuid,
          items: [{
            file_name: file.name,
            content_type: file.type.toLowerCase(),
            size_bytes: file.size
          }]
        })
      }
    ).pipe(
      switchMap((payload) => {
        const ticket = payload?.items?.[0];
        if (!ticket) {
          return throwError(() => new AiChatApiError({
            status: 0,
            apiMessage: 'That image could not be prepared for upload. Please try again.'
          }));
        }
        return this.postToS3(ticket, file).pipe(map(() => ticket.attachment_uuid));
      }),
      switchMap((attachmentUuid: string) =>
        this.authedJson<{ items: ConfirmedAttachment[] }>(
          ApiConstants.AI_CHAT_ATTACHMENT_CONFIRM_URL,
          { method: 'POST', body: JSON.stringify({ attachment_uuids: [attachmentUuid] }) }
        )
      ),
      switchMap((payload) => {
        const confirmed = payload?.items?.[0];
        // 'rejected' is a normal outcome, not a transport failure: it is how the service reports
        // that the stored bytes were not the image the request claimed.
        if (!confirmed || confirmed.status !== 'ready') {
          return throwError(() => new AiChatApiError({
            status: 0,
            apiMessage: confirmed?.error?.trim() || 'That image was not accepted. Please try another.'
          }));
        }
        return of({
          attachment_uuid: confirmed.attachment_uuid,
          file_name: confirmed.file_name,
          content_type: confirmed.content_type,
          size_bytes: confirmed.size_bytes,
          width: confirmed.width ?? null,
          height: confirmed.height ?? null,
          url: confirmed.url ?? null
        } as AiChatAttachmentModel);
      })
    );
  }

  /**
   * Mints a fresh view URL for an image already sent.
   *
   * The URL a send returns is presigned and expires, and is never persisted with the transcript -
   * a stored one would be dead by the next visit. So a restored conversation asks for a new one
   * per image rather than rendering a broken thumbnail.
   */
  resolveAttachmentUrl(attachmentUuid: string): Observable<string> {
    return this.authedJson<{ url?: string }>(
      `${ApiConstants.AI_CHAT_ATTACHMENTS_URL}/${encodeURIComponent(attachmentUuid)}/url`,
      { method: 'GET' }
    ).pipe(map((payload) => payload?.url ?? ''));
  }

  /** Discards an image the customer removed before sending. Best-effort. */
  deleteAttachment(attachmentUuid: string): Observable<void> {
    return this.authedRequest(
      `${ApiConstants.AI_CHAT_ATTACHMENTS_URL}/${encodeURIComponent(attachmentUuid)}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Posts the file to S3 under the signed policy.
   *
   * Unauthenticated on purpose - the signature in `fields` IS the authorisation, and sending our
   * bearer token to a third-party host would leak it. The field order matters: S3 ignores every
   * form field that appears after `file`, so the policy fields have to go on first.
   */
  private postToS3(ticket: AttachmentUploadTicket, file: File): Observable<void> {
    return new Observable<void>((subscriber) => {
      const controller = new AbortController();
      const form = new FormData();

      Object.entries(ticket.fields ?? {}).forEach(([key, value]) => form.append(key, value));
      form.append('file', file);

      fetch(ticket.post_url, { method: 'POST', body: form, signal: controller.signal })
        .then((response: Response) => {
          if (!response.ok) {
            // S3 answers with an XML error document, which is of no use to a customer.
            throw new AiChatApiError({
              status: response.status,
              apiMessage: 'That image could not be uploaded. Please try again.'
            });
          }
          subscriber.next();
          subscriber.complete();
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }
          // NOT toApiError: this leg goes to S3, not to the assistant, and its generic message
          // ("We could not reach the assistant") sent the customer looking in the wrong place.
          // A rejected fetch() here has no status and no readable body - the browser reports a
          // cross-origin refusal exactly like an offline one - and the usual cause is the bucket's
          // CORS policy not listing this website's origin, which no retry can get past.
          subscriber.error(error instanceof AiChatApiError ? error : new AiChatApiError({
            status: 0,
            apiMessage: 'That image could not be uploaded. Please try again.'
          }));
        });

      return () => controller.abort();
    });
  }

  /**
   * One authenticated JSON call that returns the envelope's `data` payload.
   */
  private authedJson<T>(path: string, init: RequestInit): Observable<T> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new AiChatApiError({
        status: 0,
        apiMessage: 'This action is only available in the browser.'
      }));
    }

    return this.serviceTokenService.ensureToken().pipe(
      switchMap((token: string | null) => new Observable<T>((subscriber) => {
        const controller = new AbortController();

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Auth-Client': 'frontend'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        fetch(`${environment.aiApiUrl}${path}`, {
          ...init,
          headers,
          signal: controller.signal
        })
          .then(async (response: Response) => {
            if (!response.ok) {
              throw await this.buildApiError(response);
            }
            const body = await response.json();
            // Every AI-service route answers in the same envelope and the contents are under
            // `payload` - confirmed against the service's own OpenAPI schema, where every 200 is
            // a BaseResponse of {status, message, payload, error_details, status_code}.
            //
            // This read `body.data` and fell back to the whole envelope when that was missing,
            // which is what it always was. Callers then looked for their fields on the envelope:
            // uploadAttachment saw no `items` and reported every pick as "could not be prepared
            // for upload", and resolveAttachmentUrl saw no `url` and returned '' for every image
            // in a restored conversation. `data` is kept as a fallback only so this keeps working
            // if a route is ever moved onto that key.
            subscriber.next((body?.payload ?? body?.data ?? body) as T);
            subscriber.complete();
          })
          .catch((error: unknown) => {
            if (error instanceof DOMException && error.name === 'AbortError') {
              return;
            }
            subscriber.error(this.toApiError(error));
          });

        return () => controller.abort();
      }))
    );
  }

  /**
   * One authenticated JSON call against the AI service, completing on 2xx.
   *
   * fetch(), not HttpClient: httpInterceptor rewrites every HttpClient URL onto the v3/Node API
   * hosts, which would send these to the wrong service entirely. Auth is the same pair the
   * stream uses - the Bearer token proves who is calling, `X-Auth-Client` names the key that
   * verifies it, and a token without the header fails closed at 401.
   *
   * @param path relative to aiApiUrl, query string included
   */
  private authedRequest(path: string, init: RequestInit): Observable<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new AiChatApiError({
        status: 0,
        apiMessage: 'This action is only available in the browser.'
      }));
    }

    return this.serviceTokenService.ensureToken().pipe(
      switchMap((token: string | null) => new Observable<void>((subscriber) => {
        const controller = new AbortController();

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Auth-Client': 'frontend'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        fetch(`${environment.aiApiUrl}${path}`, {
          ...init,
          headers,
          signal: controller.signal
        })
          .then(async (response: Response) => {
            if (!response.ok) {
              throw await this.buildApiError(response);
            }
            subscriber.next();
            subscriber.complete();
          })
          .catch((error: unknown) => {
            if (error instanceof DOMException && error.name === 'AbortError') {
              return;
            }
            subscriber.error(this.toApiError(error));
          });

        return () => controller.abort();
      }))
    );
  }

  /**
   * Opens the SSE request and pumps frames into the subscriber.
   */
  private openStream(
    conversationUuid: string,
    messageUuid: string,
    message: string,
    token: string | null,
    regenerate: boolean,
    attachmentUuids: string[]
  ): Observable<AiChatStreamFrame> {
    return new Observable<AiChatStreamFrame>((subscriber) => {
      const controller = new AbortController();
      let isCancelled = false;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        // Names which key verifies the token. Must be exactly 'frontend'.
        'X-Auth-Client': 'frontend'
      };
      // A local dev service checks neither header, so a missing token is not fatal there; on
      // every deployed environment the service answers 401 and the caller signs the user in again.
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      fetch(`${environment.aiApiUrl}${ApiConstants.AI_CHAT_PUBLIC_CHAT_URL}`, {
        method: 'POST',
        headers,
        // No `model`: the public channel picks the model from its own environment and the
        // request schema has no field for one, by design. `attachment_uuids` is sent only when
        // there are images - the schema defaults it to an empty list.
        body: JSON.stringify({
          message_uuid: messageUuid,
          message,
          conversation_uuid: conversationUuid,
          ...(attachmentUuids.length ? { attachment_uuids: attachmentUuids } : {}),
          ...(regenerate ? { regenerate: true } : {})
        }),
        signal: controller.signal
      })
        .then(async (response: Response) => {
          // A refusal is always a plain JSON envelope sent before the stream opens, so the
          // status alone decides whether there is a stream to read at all.
          if (!response.ok) {
            throw await this.buildApiError(response);
          }
          if (!response.body) {
            throw new AiChatApiError({
              status: response.status,
              apiMessage: 'The assistant sent an empty response. Please try again.'
            });
          }
          // A 200 that is not an event stream means the turn died *after* the status line was
          // sent - the agent raised once streaming had already begun, so the body carries an
          // error envelope instead of `data:` frames. Observed on staging when the agent's
          // checkpointer could not be reached. Without this the frames simply never parse and
          // the turn looks like an empty answer rather than the failure it is.
          if (!(response.headers.get('Content-Type') ?? '').includes('text/event-stream')) {
            // Reported as a server fault, not as the 200 it arrived as: the envelope's message
            // is an internal traceback summary ("unhandled errors in a TaskGroup"), and only the
            // 5xx path keeps that away from the customer while still logging it.
            throw await this.buildApiError(response, 502);
          }
          await this.readStream(response.body, subscriber, () => isCancelled);
          subscriber.complete();
        })
        .catch((error: unknown) => {
          // An abort is our own teardown (component destroyed, user pressed Stop), not a failure.
          if (isCancelled || (error instanceof DOMException && error.name === 'AbortError')) {
            return;
          }
          subscriber.error(this.toApiError(error));
        });

      return () => {
        isCancelled = true;
        controller.abort();
      };
    });
  }

  /**
   * Decodes the response body and emits one frame per `data:` payload.
   *
   * Frames are delimited by a blank line and a single frame may span several `data:` lines, so
   * the buffer is split on the blank line and the payload lines re-joined - splitting on "\n"
   * alone would truncate any answer containing one.
   */
  private async readStream(
    body: ReadableStream<Uint8Array>,
    subscriber: { next: (frame: AiChatStreamFrame) => void },
    isCancelled: () => boolean
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      for (; ;) {
        const { done, value } = await reader.read();
        if (done || isCancelled()) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Keep the trailing fragment: a chunk boundary can land mid-frame.
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() ?? '';

        for (const block of blocks) {
          const frame = this.parseFrame(block);
          if (frame) {
            subscriber.next(frame);
          }
        }
      }

      // The service may close without a trailing blank line, leaving the last frame in the buffer.
      const trailing = this.parseFrame(buffer + decoder.decode());
      if (trailing && !isCancelled()) {
        subscriber.next(trailing);
      }
    } finally {
      // Releasing the lock lets an aborted request tear its body down cleanly.
      reader.releaseLock();
    }
  }

  /**
   * Turns one SSE block into a frame, or null if it holds nothing usable (comment/keep-alive
   * lines, blank blocks, or a payload that is not the documented JSON object).
   */
  private parseFrame(block: string): AiChatStreamFrame | null {
    const payload = block
      .split(/\r?\n/)
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n')
      .trim();

    if (!payload || payload === '[DONE]') {
      return null;
    }

    try {
      const parsed = JSON.parse(payload);
      if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
        return null;
      }
      return { type: parsed.type, data: parsed.data ?? {} } as AiChatStreamFrame;
    } catch {
      // A malformed frame must not kill a stream that is otherwise fine.
      return null;
    }
  }

  /**
   * Reads the error envelope off a non-2xx response. Every refusal uses the same five keys,
   * with `payload` populated on 429 and `error_details` on 403/422/429.
   */
  private async buildApiError(response: Response, statusOverride?: number): Promise<AiChatApiError> {
    let envelope: ApiErrorEnvelope | null = null;
    try {
      envelope = await response.json();
    } catch {
      // A gateway or proxy failure can answer with HTML; fall through to the generic message.
    }

    return new AiChatApiError({
      status: statusOverride ?? response.status,
      apiMessage: typeof envelope?.message === 'string' && envelope.message.trim().length
        ? envelope.message.trim()
        : 'We could not reach the assistant. Please try again.',
      retryAt: envelope?.payload?.retry_at ?? null,
      retryAfterSeconds: envelope?.payload?.retry_after_seconds ?? null,
      requestUuid: envelope?.error_details?.request_uuid ?? null
    });
  }

  /**
   * Normalises anything thrown inside the fetch chain into an AiChatApiError, so callers only
   * ever branch on `status`. A network failure (offline, DNS, CORS) has no status - it is 0.
   */
  private toApiError(error: unknown): AiChatApiError {
    if (error instanceof AiChatApiError) {
      return error;
    }
    return new AiChatApiError({
      status: 0,
      apiMessage: 'We could not reach the assistant. Please check your connection and try again.'
    });
  }

  /**
   * Generates the v4 `message_uuid` for one turn. Required by the API on every request, and
   * generated per turn rather than per message object: a regenerated answer must go up under a
   * fresh uuid, or the service overwrites the answer it already stored against the old one.
   */
  createMessageUuid(): string {
    return this.createUuid();
  }

  /**
   * Generates the v4 `conversation_uuid`. randomUUID needs a secure context, so fall back to a
   * Math.random-based v4 on plain-HTTP dev hosts - the value only has to be unique per session.
   */
  private createConversationUuid(): string {
    return this.createUuid();
  }

  /** v4 UUID, with a non-crypto fallback for plain-HTTP dev hosts. */
  private createUuid(): string {
    if (isPlatformBrowser(this.platformId) && typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0;
      const value = char === 'x' ? random : (random & 0x3 | 0x8);
      return value.toString(16);
    });
  }
}
